import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import {
    getConfig,
    DocumentType,
    AppConfig,
} from '@superbet-group/web.lib.config';
import {
    Locale,
    TransactionType,
} from '@superbet-group/web.types';
import {
    RestClientError,
    RestClientErrorType,
} from '@superbet-group/web.lib.rest';
import {
    IResponse,
    AuthenticationError,
    RequestError,
    ValidationError,
    instanceGuard,
    failure,
    success,
} from '@superbet-group/web.lib.utilities';

import { smsLimitNotice } from '@core/constants';
import LimitError from '@core/errors/user/LimitError';
import NotificationActionError from '@core/errors/user/NotificationActionError';
import { transformBalance } from '@core/mappers/user/transformBalance';
import { transformHistory } from '@core/mappers/user/transformHistory';
import { transformMessage } from '@core/mappers/user/pushNotifications/transformMessage';
import { transformWallet } from '@core/mappers/user/transformWallet';
import { transformWithdrawalResponse } from '@core/mappers/user/transformWithdrawalResponse';
import IMessageDataDto from '@core/mappers/user/dtos/IMessageDataDto';
import {
    TransaksWithdrawalResponseDto,
    WithdrawalResponseDto,
} from '@core/mappers/user/dtos/WithdrawalResponseDto';
import { LimitType } from '@core/models/user/Limit';
import DepositEligibility from '@core/models/user/deposit/DepositEligibility';
import Message from '@core/models/user/pushNotifications/Message';
import {
    WithdrawalAbility,
    WithdrawalAbilityDto,
} from '@core/models/user/withdrawal/WithdrawalAbility';
import { getDetails } from '@core/services/authentication/helpers';
import SingletonError from '@core/services/common/errors/SingletonError';
import { AssembledMarketingAttributionData } from '@core/services/marketing/_private/types';
import SmsLimitReachedError from '@errors/user/SmsLimitReachedError';
import Balance from '@models/user/Balance';
import User from '@models/user/User';
import {
    PlayerDetailsFieldType,
    WithdrawalType,
} from '@models/user/enums';
import History, { HistoryCategory } from '@models/user/history/History';
import WithdrawalRequest from '@models/user/withdrawal/WithdrawalRequest';
import WithdrawalResponse from '@models/user/withdrawal/WithdrawalResponse';
import { pickUserByFields } from '@store/modules/data/authentication/helpers';
import * as NetworkUtils from '@utils/network/networkHelpers';

import RestService from './_private/RestService';
import {
    IdentityDataPayload,
    PrivacyPolicyData,
    RegistrationSecondStepParams,
    LicencePreferencesPayload,
    LicencePreferencesResponse,
    VerificationConfigPayload,
    SerProPayload,
    SerProResponse,
    VerificationRequestParams,
} from '@core/services/user/_private/Types';
import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import { BgcLicence } from '@accountFederation/modules/licencePreferences/types';
import DepositError from '@core/errors/user/DepositError';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { LoginHistoryEntry } from '@accountFederation/modules/settings/loginHistory/types';
import { mapLoginHistoryItems } from '@accountFederation/modules/settings/loginHistory/loginHistoryMapper';
import {
    isPoland,
    isRomania,
} from '@modules/layout/utils/isCountry';
import TransaksError from '@core/errors/user/TransaksError';
import {
    BankAccount,
    RegisterBankAccountRequest,
    UnregisterBankAccountRequest,
} from '@core/models/user/Wallet';
import {
    ErrorCode,
    getTranslatedErrorCode,
} from '@accountFederation/utils/mappers/translationsCodes';
import { getWithdrawalAbility } from './WithdrawalAbility';
import { WalletDto } from '@core/mappers/user/dtos/WalletDto';
import { BalanceDto } from '@core/mappers/user/dtos/BalanceDto';

const isWalletBreakdownEnabled = getConfig().countrySpecific.user.withdrawal.walletBreakdown.isEnabled;
const hasBankTransferEndpoint = getConfig().countrySpecific.user.withdrawal.hasBankTransferEndpoint;

export type { WithdrawalRequest };

const sportLicenceKey = 'acceptedSportLicence';

export enum UserServiceMemoryKey {
    GetPlayerDetails = 'getPlayerDetails',
    GetPlayerBalance = 'getPlayerBalance',
    CheckDepositEligibility = 'checkDepositEligibility',
    CheckWithdrawalEligibility = 'checkWithdrawalEligibility',
}

export class UserService {
    private static instance?: UserService;
    public responseMemory: { [key: string]: any } = {};

    public static getInstance(): UserService {
        return instanceGuard(UserService.instance);
    }

    public static createInstance(config: AppConfig) {
        if (!UserService.instance) {
            UserService.instance = new UserService(config);
        }
        return UserService.instance;
    }

    private restService: RestService;

    private constructor(private config: AppConfig) {
        if (UserService.instance) {
            throw new SingletonError(this.constructor.name);
        }
        this.restService = new RestService(this.config);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getUserDetails(fields?: PlayerDetailsFieldType[]): Promise<Partial<User>> {
        try {
            const response = await this.restService.getUserDetails(fields);

            if (response.errorCode) {
                response.error = true;
            }

            if (response.error) {
                throw new AuthenticationError();
            }

            this.responseMemory[UserServiceMemoryKey.GetPlayerDetails] = response;
            return pickUserByFields(getDetails(response.data as any), fields); // TODO remove any: "Reference User Model"
        } catch (error: any) {
            // moved this in a try/catch because Betler can return 4xx status code
            if (error.type === RestClientErrorType.AuthenticationNeeded) {
                throw new AuthenticationError();
            } else {
                throw error;
            }
        }
    }

    /**
     * Comtrade and Betler provide balance data differently and this needed to be mapped on different ways
     *
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getPlayerBalance(): Promise<Balance | null> {
        const response = isWalletBreakdownEnabled
            ? await this.restService.getWalletBreakdown()
            : await this.restService.getPlayerBalance();

        this.responseMemory[UserServiceMemoryKey.GetPlayerBalance] = response;

        if (isWalletBreakdownEnabled) {
            return transformWallet(response.data as WalletDto);
        } else {
            return transformBalance(response.data as BalanceDto);
        }
    }

    public async setLocale(locale: Locale): Promise<void> {
        await this.restService.setLocale(locale);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async createDeposit(amount: number, paySafeCard = false, isPaymentIq = false, resultPagePath?: string, session?: string, sessionId?: string, isQuickDeposit?: boolean, customProvider?: string | null, routedParams?: Record<string, any> | null): Promise<{ paymentUrl: string, transactionId?: string, appUrl?: string }> {
        let response: any;
        try {
            response = await this.restService.createDeposit(amount, paySafeCard, isPaymentIq, resultPagePath, session, sessionId, isQuickDeposit, customProvider, routedParams);
        } catch (e) {
            if (e instanceof RestClientError) {
                // processResponse hides the inner error in the `message` field of a new error embedded into RestClientError
                const innerError = JSON.parse(e.error.message || '{}');
                if (innerError.data?.DeclineReasonCode === LimitError.code && shellLocalConfig.deposit?.hasDepositLimitReachedModal) {
                    throw new LimitError(LimitType.Deposit);
                }
                if (innerError.data?.PaymentStatus === 'Failed') {
                    throw new DepositError(innerError.data?.ExternalErrorCode, innerError.data?.ExternalErrorMessage);
                }
            }
        }

        if (response.PaymentStatus === 'Failed') {
            throw new DepositError(response.ExternalErrorCode, response.ExternalErrorMessage);
        }

        if (response.data.PaymentStatus === 'Declined') {
            throw new ValidationError({
                nonField: [response.data.DeclineReason],
            });
        }

        if (isQuickDeposit) {
            return {
                paymentUrl: response.data.DepositData,
                transactionId: response.data.TransactionId,
                appUrl: response.data.APPLink,
            };
        }

        return {
            paymentUrl: response.data.RedirectURL,
        };
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getRegisteredBanks(): Promise<BankAccount[]> {
        return await this.restService.getRegisteredBanks();
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async registerBankAccount(registerBankAccountRequest: RegisterBankAccountRequest): Promise<any> {
        return await this.restService.registerBankAccount(registerBankAccountRequest);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async unregisterBankAccount(unregisterBankAccountRequest: UnregisterBankAccountRequest): Promise<any> {
        return await this.restService.unregisterBankAccount(unregisterBankAccountRequest);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async checkDepositEligibility(): Promise<DepositEligibility> {
        const response = await this.restService.checkDepositEligibility();
        this.responseMemory[UserServiceMemoryKey.CheckDepositEligibility] = response;

        const {
            EligibleForOnlineDeposit: isOnlineEligible,
            EligibleForBankTransferDeposit: isBankTransferEligible,
            EligibleForCicoDeposit: isShopEligible,
        } = response.data;

        return {
            isOnlineEligible,
            isBankTransferEligible,
            isShopEligible,
        };
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async checkWithdrawalAbility(): Promise<WithdrawalAbility> {
        const url = window.withdrawalAbilityUrl;

        if (url) {
            const dto = (await fetch(url)).body as unknown as WithdrawalAbilityDto;
            return getWithdrawalAbility(dto);
        }

        const response = await this.restService.checkWithdrawalEligibility();
        this.responseMemory[UserServiceMemoryKey.CheckWithdrawalEligibility] = response;

        return getWithdrawalAbility(response.data);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async createWithdrawal(withdrawalRequest: WithdrawalRequest, resultPagePath: string, session?: string, sessionId?: string, otp?: string): Promise<WithdrawalResponse> {
        let response: IResponse<WithdrawalResponseDto>;

        if (hasBankTransferEndpoint && withdrawalRequest.type === WithdrawalType.BankTransfer) {
            response = await this.restService.createBankTransferWithdrawal(withdrawalRequest, session, sessionId);
        } else if ([WithdrawalType.Online, WithdrawalType.OnlinePix, WithdrawalType.BankTransfer, WithdrawalType.PaySafeCard, WithdrawalType.PaymentIQ]
            .includes(withdrawalRequest.type)
        ) {
            response = await this.restService.createOnlineWithdrawal(withdrawalRequest, resultPagePath, session, sessionId, otp);
        } else if (
            withdrawalRequest.type === WithdrawalType.Transaks && withdrawalRequest.transaksAccount
        ) {
            const res = { data: {} as TransaksWithdrawalResponseDto };
            res.data = await this.restService.createTransaction(withdrawalRequest.amount, withdrawalRequest.transaksAccount, TransactionType.Withdrawal, session, sessionId);
            if (res.data.response === 'error') {
                throw new TransaksError(res.data.reason);
            } else {
                response = { data: res as unknown as WithdrawalResponseDto };
            }
        } else {
            response = await this.restService.createShopWithdrawal(withdrawalRequest, session, sessionId);
        }
        return transformWithdrawalResponse(response.data, withdrawalRequest.type);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async createTransaction(amount: number, targetId: string, action: TransactionType, session?: string, sessionId?: string): Promise<any> {
        let response: any;
        try {
            response = await this.restService.createTransaction(amount, targetId, action, session, sessionId);
        } catch (e) {
            if (e instanceof RestClientError) {
                const innerError = typeof e.error.message === 'string'
                    ? e.error.message
                    : JSON.parse(e.error.message || '{}');

                if (
                    innerError.data?.DeclineReasonCode === LimitError.code
                    && action === TransactionType.Deposit
                    && shellLocalConfig.deposit?.hasDepositLimitReachedModal
                ) {
                    throw new LimitError(LimitType.Deposit);
                }
                if (innerError.data?.PaymentStatus === 'Failed') {
                    throw new DepositError(innerError.data?.ExternalErrorCode, innerError.data?.ExternalErrorMessage);
                }
            }
        }

        return response;
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async fetchTransactions(page: number, pageSize: number): Promise<History[]> {
        const response = await this.restService.fetchTransactions(page, pageSize);
        return response.data.map((dto) => transformHistory(dto, {
            isFetchable: true,
            category: HistoryCategory.Transactions,
        }));
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async fetchAllTransactions(page: number, pageSize: number): Promise<History[]> {
        const response = await this.restService.fetchAllTransactions(page, pageSize);
        return response.data;
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async fetchAllHistoryTransactions(): Promise<any> {
        return await this.restService.fetchAllHistoryTransactions();
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async fetchCasinoPlays(page: number, pageSize: number): Promise<History[]> {
        const response = await this.restService.fetchCasinoPlays(page, pageSize);
        return response.data.map((dto) => transformHistory(dto, {
            isFetchable: false,
            category: HistoryCategory.Casino,
        }));
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async fetchVirtualsPlays(page: number, pageSize: number): Promise<History[]> {
        const response = await this.restService.fetchVirtualsPlays(page, pageSize);
        return response.data.map((dto) => transformHistory(dto, {
            isFetchable: false,
            category: HistoryCategory.Bets,
        }));
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async fetchBets(page: number, pageSize: number): Promise<History[]> {
        const response = await this.restService.fetchBets(page, pageSize);
        return response.data.map((dto) => transformHistory(dto, {
            isFetchable: true,
            category: HistoryCategory.Bets,
        }));
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public fetchMessages(): Promise<Message[]> {
        return UserService.messageAction(this.restService.fetchMessages());
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async respondToMessage(actionId: number): Promise<void> {
        const response = await this.restService.respondToMessage(actionId);

        if (['ActionAlreadyCompleted', 'PopupActionDoesNotExist'].includes(response.errorCode)) {
            throw new NotificationActionError(response.notice);
        }
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async cancelWithdrawal(transaction: History): Promise<any> {
        const response = await this.restService.cancelWithdrawal(transaction);

        if (response.data.StatusCode !== 'Success') {
            throw new RequestError(new Error(JSON.stringify(response)));
        }

        return response;
    }

    public async updateUsername(username: string): Promise<any> {
        const response = await this.restService.updateUsername(username);
        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            } else if (response.fieldErrors && response.fieldErrors.username) {
                throw new ValidationError({
                    username: [response.fieldErrors.username],
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async updatePhone(phone: string): Promise<any> {
        const response = await this.restService.updatePhone(phone);

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }

            const dupePhoneError = response.errors?.find((error: ErrorCode) => error.field === 'phone' && error.code === 707);
            // Tmp fix for selective translated errorcodes from betler
            if (dupePhoneError) {
                throw new ValidationError({
                    phone: [getTranslatedErrorCode(dupePhoneError)],
                });
            }

            if (response.fieldErrors && response.fieldErrors.phone) {
                throw new ValidationError({
                    phone: [response.fieldErrors.phone],
                });
            } else if (response.notice) {
                if (response.notice === smsLimitNotice) {
                    throw new SmsLimitReachedError();
                } else {
                    throw new ValidationError({
                        general: [response.notice],
                    });
                }
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async updateBrasilMigrationDetails(gender: string, nationality: string): Promise<any> {
        const response = await this.restService.updateBrasilMigrationDetails(gender, nationality);

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }

            if (response.fieldErrors && response.fieldErrors.gender) {
                throw new ValidationError({
                    gender: [response.fieldErrors.gender],
                });
            } else if (response.fieldErrors && response.fieldErrors.nationality) {
                throw new ValidationError({
                    nationality: [response.fieldErrors.nationality],
                });
            } else if (response.notice) {
                throw new ValidationError({
                    general: [response.notice],
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async uploadIdCard(file: string, fileName: string, username?: string, otc?: string): Promise<any> {
        const response = await this.restService.uploadIdCard(file, fileName, username, otc);

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }

            if (response.errorCode === 'DocumentUploadLimitExceeded') {
                throw new ValidationError({
                    file: ['Document limit exceeded. Please contact our support'],
                });
            }

            if (response.errorCode === 'DocumentFormatNotSupported') {
                throw new ValidationError({
                    file: ['document not supported'],
                });
            } else if (response.notice) {
                throw new ValidationError({
                    general: [response.notice],
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async registrationSecondStep(
        file: string,
        fileName: string,
        params: RegistrationSecondStepParams,
    ): Promise<any> {
        const response = await this.restService.registrationSecondStep(file, fileName, params);

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }

            if (response.errorCode === 'DocumentUploadLimitExceeded') {
                throw new ValidationError({
                    file: ['Document limit exceeded. Please contact our support'],
                });
            }

            if (response.errorCode === 'DocumentFormatNotSupported') {
                throw new ValidationError({
                    file: ['document not supported'],
                });
            } else if (response.notice) {
                throw new ValidationError({
                    general: [response.notice],
                });
            } else if (response.fieldErrors) {
                Object.keys(response.fieldErrors).forEach((key: string) => {
                    response.fieldErrors[key] = [response.fieldErrors[key]];
                });
                throw new ValidationError(response.fieldErrors);
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async sendSmsCode(): Promise<any> {
        const response = await this.restService.sendSmsCode();

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }

            if (response.errorCode === 'ContactInfoVerificationRequestFailed') {
                throw new ValidationError({
                    reason: ['sending sms failure'],
                });
            } else if (response.notice) {
                throw new ValidationError({
                    reason: [response.notice],
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async validateSmsCode(code: string): Promise<any> {
        const response = await this.restService.validateSmsCode(code);

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }
            if (response.errorCode === 'VerificationCodeNotFound') {
                throw new ValidationError({
                    code: ['verification code not found'],
                });
            }
            if (response.errorCode === 'InternalError') {
                throw new ValidationError({
                    code: ['internal error'],
                });
            }
            if (
                response.errorCode === 'VerifyPlayerCodeFailed'
                || Object.keys(response).every((field) => ['error', 'notice'].includes(field)) // Handle OTP generic error
            ) {
                throw new ValidationError({
                    code: ['verify player code failed'],
                });
            } else if (response.notice) {
                throw new ValidationError({
                    general: [response.notice],
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
        AnalyticsSDK.getInstance().log('Phone_Verified');
    }

    public updatePrivacy(data: PrivacyPolicyData): Promise<any> {
        return this.restService.updatePrivacy(data);
    }

    public getPlayerSettings(settingName: string): Promise<any> {
        return this.restService.getPlayerSettings(settingName);
    }

    public setPlayerSettings(settingName: string, settingValue: Record<string, unknown>| number): Promise<any> {
        return this.restService.setPlayerSettings(settingName, settingValue);
    }

    public async sendActivationEmail(emailAddress: string) {
        const response = await this.restService.sendActivationEmail(emailAddress);
        const isComtrade = isRomania() || isPoland();

        if (response.error) {
            const code = isComtrade ? response.errorCode : response.data.StatusCode;
            let message = '';

            switch (code) {
                case 'PlayerNotFoundByEmail':
                case 'InvalidPlayerStatusForSendActivationEmail':
                    message = 'pam.resendActivationEmail.invalidEmail';
                    break;
                case 'MultipleEntriesForNumberOfEmailActivations':
                case 'NumberOfEmailActivationsNotDefined':
                case 'SendActivationEmailLimitReached':
                case 'InvalidActivationToken':
                case 'NotAllowedActivation':
                    message = 'pam.resendActivationEmail.limitError';
                    break;
                case 'SendActivationEmailFailed':
                case 'InternalError':
                default:
                    message = 'pam.resendActivationEmail.error';
                    break;
            }

            throw new Error(message);
        }
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    private static async messageAction(responsePromise: Promise<any>) {
        const response = await responsePromise as IResponse<IMessageDataDto>;
        const messages = response.data.Messages.map((message) => ({ Message: message, StatusCode: response.data.StatusCode }));
        return messages.map(transformMessage);
    }

    public static clearInstance() {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete UserService.instance;
    }

    public updateCustomParams(customParameters: AssembledMarketingAttributionData): Promise<any> {
        return this.restService.updateCustomParams(customParameters);
    }

    public async sendIdentityData(data: IdentityDataPayload) {
        const response = await this.restService.sendIdentityData(data);
        if (response.error) {
            if (response.fieldErrors) {
                const fieldErrors: Record<string, string[]> = {};

                Object.keys(response.fieldErrors).forEach((key: string) => {
                    if (key.toUpperCase() === DocumentType.Passport.toUpperCase()) {
                        fieldErrors.documentNumber = [response.fieldErrors[key]];
                        delete response.fieldErrors[key];
                    } else {
                        fieldErrors[key] = [response.fieldErrors[key]];
                    }
                });
                return failure(new ValidationError(fieldErrors));
            } else {
                return failure(new ValidationError({
                    general: [response.notice],
                }));
            }
        }
        return success(response);
    }

    public async getUserLicencePreferences(): Promise<LicencePreferencesResponse> {
        const response = await this.restService.getUserLicencePreferences();
        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }

        // Caching sport licence because it's frequently reused when submitting a ticket
        const acceptedSportLicence = response.data.licences[BgcLicence.Sport]?.accepted;
        if (acceptedSportLicence) {
            clientStorageWithExpiry.set(sportLicenceKey, true);
        } else {
            clientStorageWithExpiry.remove(sportLicenceKey);
        }

        return response;
    }

    public async updateUserLicencePreferences(data: LicencePreferencesPayload): Promise<{ error: boolean }> {
        const response = await this.restService.updateUserLicencePreferences(data);
        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }

        // Caching sport licence because it's frequently reused when submitting a ticket
        const acceptedSportLicence = data.licences[BgcLicence.Sport];
        if (acceptedSportLicence) {
            clientStorageWithExpiry.set(sportLicenceKey, true);
        } else {
            clientStorageWithExpiry.remove(sportLicenceKey);
        }

        return response;
    }

    public async getVerificationRequest(params: VerificationRequestParams): Promise<VerificationConfigPayload> {
        let response;

        if (params.action === 'faceid' && params.isAnonymous) {
            response = await this.restService.getAnonymousIdentityVerificationRequest(params);
        } else {
            response = await this.restService.getIdentityVerificationRequest(params);
        }

        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }

        return response.data;
    }

    public async getRegulatorData(payload: SerProPayload): Promise<SerProResponse['data']> {
        const response = await this.restService.getRegulatorData(payload);

        if (response.error) {
            const errorCode: number = response.errors?.[0]?.code ?? 0;
            if (errorCode === 9000) {
                throw new ValidationError({
                    reason: ['pam.cfpValidation.error'],
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }

        return response.data;
    }

    public async getLoginHistory(): Promise<LoginHistoryEntry[]> {
        const response = await this.restService.getLoginHistory();

        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }

        return mapLoginHistoryItems(response.data);
    }
}
